import { useState, useMemo } from 'react'
import { parseTraceData, type TraceNode } from '@/lib/utils/traceParser'
import {
    ChevronRight, ChevronDown,
    Database, Brain, Layers, Wrench,
    CheckCircle2, XCircle, AlertTriangle, Clock
} from 'lucide-react'

// Props
interface TraceTreeProps {
    data: any
    onNodeSelect?: (node: TraceNode) => void
    selectedNodeId?: string
}

// Icon mapping
const TypeIcon = ({ type }: { type: TraceNode['type'] }) => {
    switch (type) {
        case 'llm': return <Brain className="w-4 h-4 text-purple-400" />
        case 'retriever': return <Database className="w-4 h-4 text-blue-400" />
        case 'tool': return <Wrench className="w-4 h-4 text-orange-400" />
        default: return <Layers className="w-4 h-4 text-gray-400" />
    }
}

// Single Node Component (Recursive)
const TreeNode = ({ node, onSelect, selectedId }: { node: TraceNode, onSelect?: (n: TraceNode) => void, selectedId?: string }) => {
    const [isExpanded, setIsExpanded] = useState(true)
    const hasChildren = node.children && node.children.length > 0
    const isSelected = selectedId === node.id

    const handleToggle = (e: React.MouseEvent) => {
        e.stopPropagation()
        setIsExpanded(!isExpanded)
    }

    return (
        <div className="flex flex-col">
            {/* Node Row */}
            <div
                className={`flex items-center gap-2 py-2 px-2 rounded-lg cursor-pointer transition-colors border-l-2
                    ${isSelected ? 'bg-white/10 border-blue-500' : 'hover:bg-white/5 border-transparent'}
                    ${node.status === 'error' ? 'bg-red-500/10 border-l-red-500' : ''}
                `}
                style={{ marginLeft: `${node.depth * 20}px` }}
                onClick={() => onSelect?.(node)}
            >
                {/* Expander */}
                <button
                    onClick={handleToggle}
                    className={`p-1 rounded hover:bg-white/10 text-gray-400 ${hasChildren ? 'visible' : 'invisible'}`}
                >
                    {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                </button>

                {/* Icon */}
                <div className="p-1.5 bg-black/40 rounded border border-white/10">
                    <TypeIcon type={node.type} />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0 flex items-center justify-between gap-4">
                    <div className="flex flex-col">
                        <span className={`text-sm font-medium truncate ${node.status === 'error' ? 'text-red-400' : 'text-gray-200'}`}>
                            {node.name}
                        </span>
                        <span className="text-[10px] text-gray-500 font-mono capitalize">
                            {node.type}
                        </span>
                    </div>

                    {/* Metrics */}
                    <div className="flex items-center gap-4 text-xs text-gray-500 font-mono">
                        {node.latency && (
                            <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {node.latency.toFixed(0)}ms
                            </span>
                        )}
                        {/* Status Icon */}
                        {node.status === 'success' && <CheckCircle2 className="w-3 h-3 text-green-500/50" />}
                        {node.status === 'error' && <XCircle className="w-3 h-3 text-red-500" />}
                        {node.status === 'warning' && <AlertTriangle className="w-3 h-3 text-yellow-500" />}
                    </div>
                </div>
            </div>

            {/* Children Render */}
            {isExpanded && hasChildren && (
                <div className="flex flex-col relative">
                    {/* Vertical Guide Line */}
                    <div
                        className="absolute left-0 bottom-0 top-0 w-px bg-white/5"
                        style={{ left: `${node.depth * 20 + 11}px` }}
                    />
                    {node.children.map(child => (
                        <TreeNode
                            key={child.id}
                            node={child}
                            onSelect={onSelect}
                            selectedId={selectedId}
                        />
                    ))}
                </div>
            )}
        </div>
    )
}

export function TraceTree({ data, onNodeSelect, selectedNodeId }: TraceTreeProps) {
    const rootNode = useMemo(() => parseTraceData(data), [data])

    if (!rootNode) return null

    return (
        <div className="w-full pb-10">
            <TreeNode
                node={rootNode}
                onSelect={onNodeSelect}
                selectedId={selectedNodeId}
            />
        </div>
    )
}
